import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, ExternalLink, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useShopPath } from '@/hooks/useShopPath';
import { useChannelActions } from '@/hooks/useChannelActions';
import { cn } from '@/lib/utils';
import { facebookApi, findOrCreateCatalog, findPages, loadBusinesses, loadFacebookSdk, openFBLogin } from '@/lib/facebook-sdk';
import myShopClient from '@services/nearlive';

const CHANNEL_ID = 'meta-commerce';

// Matches both `facebook-domain-verification` and the newer `meta-` prefix —
// Meta has rolled the meta tag through both names at various points.
// Exported so the regex (the gate on the Step 2 sub-step 2 submit) can be
// unit-tested — see MetaCommerceSetup.test.jsx.
export const META_TAG_REGEX = /<meta name="(facebook|meta)-domain-verification" content="(?<code>\w{16,32})"(\s+)?\/?>/im;

/**
 * Meta Commerce wizard — 3 steps:
 *
 *   1. Facebook OAuth → create/assign system user + product catalog.
 *      Status flips to `pending` once the channel record lands.
 *   2. Domain verification on Facebook (3 sub-steps: add domain → enter
 *      verification code → click verify). Status stays `pending`; channel
 *      flips to `enabled` when the user confirms in sub-step 3.
 *   3. Post-enable "Create your shop on Meta Commerce Manager" courtesy step.
 *      Shown only when the dispatcher passes `?step=create-shop`, so a later
 *      visit to /setup just redirects to /manage.
 */
export function MetaCommerceSetup({ channel, shop }) {
	const location = useLocation();
	const stepParam = new URLSearchParams(location.search).get('step');
	const hasCatalog = Boolean(channel.record?.config?.catalogId);

	if (channel.live && stepParam === 'create-shop') return <StepThree channel={channel} />;
	if (hasCatalog) return <StepTwo channel={channel} shop={shop} />;
	return <StepOne shop={shop} />;
}

function StepHeader({ index, title, subtitle }) {
	return (
		<div className="space-y-2">
			<div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-action">
				Step {index} of 3
			</div>
			<h2 className="text-lg font-bold text-foreground">{title}</h2>
			{subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
		</div>
	);
}

function StepProgress({ index }) {
	const steps = ['Connect', 'Verify domain', 'Create shop'];
	return (
		<ol className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em]">
			{steps.map((label, i) => {
				const stepNumber = i + 1;
				const done = stepNumber < index;
				const active = stepNumber === index;
				return (
					<li key={label} className="flex items-center gap-2">
						<span
							className={cn(
								'inline-flex h-6 w-6 items-center justify-center rounded-full border text-[11px]',
								done
									? 'border-success bg-success text-success-foreground'
									: active
										? 'border-action bg-action text-action-foreground'
										: 'border-border bg-card text-muted-foreground'
							)}
						>
							{done ? <Check className="h-3 w-3" /> : stepNumber}
						</span>
						<span className={cn(done || active ? 'text-foreground' : 'text-muted-foreground')}>{label}</span>
						{i < steps.length - 1 ? <span className="text-muted-foreground/50">/</span> : null}
					</li>
				);
			})}
		</ol>
	);
}

function SubStepRow({ status, title, children }) {
	const dotClass =
		status === 'completed'
			? 'border-success bg-success text-success-foreground'
			: status === 'in-progress'
				? 'border-action bg-action text-action-foreground'
				: 'border-border bg-card text-muted-foreground';
	return (
		<li className="flex gap-3">
			<span className={cn('mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px]', dotClass)}>
				{status === 'completed' ? <Check className="h-3 w-3" /> : null}
			</span>
			<div className="flex-1 space-y-3">
				<p className={cn('text-sm font-semibold', status === 'pending' ? 'text-muted-foreground' : 'text-foreground')}>{title}</p>
				{status === 'in-progress' && children ? <div className="space-y-3 text-sm text-foreground/90">{children}</div> : null}
			</div>
		</li>
	);
}

function StepOne({ shop }) {
	const { create, isBusy } = useChannelActions(shop.id);
	const busy = isBusy(CHANNEL_ID);
	const [sdkReady, setSdkReady] = useState(false);
	const [businesses, setBusinesses] = useState(null);
	const [selectedBusiness, setSelectedBusiness] = useState(null);
	const [error, setError] = useState(null);
	const [loadingMessage, setLoadingMessage] = useState(null);

	useEffect(() => {
		loadFacebookSdk(() => setSdkReady(true));
	}, []);

	const startLogin = () => {
		setError(null);
		openFBLogin({
			onAuth: async () => {
				try {
					const list = await loadBusinesses();
					setBusinesses(list);
				} catch (err) {
					setError(err?.message || 'Could not load Facebook businesses.');
				}
			},
			onCancel: () => setError('Facebook login was cancelled.'),
			onError: (message) => setError(message)
		});
	};

	const businessId = import.meta.env.VITE_NEARST_BUSINESS_ID;

	const selectBusiness = async (id) => {
		setError(null);
		setSelectedBusiness(id);
		try {
			setLoadingMessage('Linking your business account…');
			await facebookApi(`/${businessId}/managed_businesses`, 'POST', { existing_client_business_id: id });

			const userToken = window.FB.getAuthResponse()?.accessToken;
			if (!userToken) throw new Error('Facebook session expired — please log in again.');

			setLoadingMessage('Creating a system user…');
			const client = await myShopClient();
			const { data: systemUser } = await client.post(`/api/channels/${shop.id}/meta-commerce/system-user`, {
				businessId: id,
				userToken
			});

			setLoadingMessage('Creating your product catalog…');
			const catalogId = await findOrCreateCatalog(id, shop);

			await facebookApi(`/${catalogId}/assigned_users`, 'POST', {
				tasks: ['MANAGE'],
				user: systemUser.systemUserId
			});

			const facebookPages = await findPages(id);

			setLoadingMessage('Saving the connection…');
			await create(CHANNEL_ID, {
				config: {
					accessToken: systemUser.accessToken,
					catalogId,
					businessId: id,
					systemUserId: systemUser.systemUserId,
					facebookPages
				},
				status: 'pending'
			});
			// The dispatcher rerenders with the new channel state and step 2
			// takes over automatically; no navigate call needed.
		} catch (err) {
			setError(err?.message || 'Something went wrong connecting your Facebook business.');
			setLoadingMessage(null);
			setSelectedBusiness(null);
		}
	};

	const working = loadingMessage || busy;

	return (
		<div className="space-y-6">
			<StepProgress index={1} />
			<StepHeader
				index={1}
				title="Connect your Meta Business account"
				subtitle="We'll link your business to NearSt, create a system user with manage permissions, and provision a product catalog for your storefront."
			/>

			{!sdkReady ? (
				<div className="flex items-center gap-2 text-sm text-muted-foreground">
					<Loader2 className="h-4 w-4 animate-spin" /> Loading the Facebook SDK…
				</div>
			) : working ? (
				<div className="flex items-center gap-2 text-sm font-semibold text-foreground">
					<Loader2 className="h-4 w-4 animate-spin" /> {loadingMessage || 'Working…'}
				</div>
			) : !businesses ? (
				<div className="space-y-3">
					{error ? <p className="text-sm font-semibold text-destructive">{error}</p> : null}
					<Button onClick={startLogin} disabled={!sdkReady}>
						Log in with Facebook
					</Button>
					<p className="text-xs text-muted-foreground">You'll need access to the Meta Business account you want to connect.</p>
				</div>
			) : /* An empty list is truthy, so this branch has to come before the picker — without it the
			      retailer gets the "Pick which business" heading above an empty grid (NEA-5965). */
			businesses.length === 0 ? (
				<div className="space-y-4">
					{error ? <p className="text-sm font-semibold text-destructive">{error}</p> : null}
					<div className="rounded-2xl border border-border bg-card p-4 text-sm">
						<p className="font-semibold text-foreground">We couldn't find a Meta Business account on that login</p>
						<p className="mt-1 text-muted-foreground">
							The Facebook account you just used doesn't manage a Meta Business account, so there's nothing for us to connect
							your products to. Either log in with the Facebook account that manages your business, or create a business
							account on Meta first and come back here.
						</p>
					</div>
					<div className="flex flex-wrap items-center gap-3">
						<Button onClick={startLogin} disabled={!sdkReady}>
							Try a different Facebook account
						</Button>
						<a
							href="https://www.facebook.com/business/help/1710077379203657"
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1.5 text-sm font-semibold text-action hover:underline"
						>
							Create a Meta Business account <ExternalLink className="h-3.5 w-3.5" />
						</a>
					</div>
					<p className="text-xs text-muted-foreground">
						If you have more than one Facebook account, log out of Facebook in this browser before trying again — otherwise
						Facebook signs you straight back in with the same one. Still stuck? Email support@unify.near.st.
					</p>
				</div>
			) : (
				<div className="space-y-3">
					<Label>Pick which business to connect</Label>
					{error ? <p className="text-sm font-semibold text-destructive">{error}</p> : null}
					<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
						{businesses.map((biz) => (
							<button
								key={biz.id}
								type="button"
								onClick={() => selectBusiness(biz.id)}
								disabled={selectedBusiness === biz.id}
								className={cn(
									'flex items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left text-sm transition hover:border-action',
									selectedBusiness === biz.id && 'opacity-70'
								)}
							>
								{biz.profile_picture_uri ? (
									<img src={biz.profile_picture_uri} alt="" className="h-10 w-10 rounded-full object-cover" />
								) : (
									<span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
										{biz.name?.charAt(0)?.toUpperCase() || '?'}
									</span>
								)}
								<span className="font-semibold text-foreground">{biz.name}</span>
							</button>
						))}
					</div>
					<button type="button" onClick={startLogin} className="text-xs font-semibold text-action hover:underline">
						Log in with a different Facebook account
					</button>
				</div>
			)}
		</div>
	);
}

function StepTwo({ channel, shop }) {
	const { update, enable, isBusy } = useChannelActions(shop.id);
	const navigate = useNavigate();
	const { shopPath } = useShopPath();
	const [domainAdded, setDomainAdded] = useState(false);
	const [verificationSaved, setVerificationSaved] = useState(false);
	const [metaTag, setMetaTag] = useState(
		channel.record?.config?.domainVerificationCode
			? `<meta name="facebook-domain-verification" content="${channel.record.config.domainVerificationCode}" />`
			: ''
	);
	const [submittingTag, setSubmittingTag] = useState(false);
	const [finishing, setFinishing] = useState(false);
	const [error, setError] = useState(null);

	const businessId = channel.record?.config?.businessId;
	const shopUrl = `${shop.slug}.near.st`;
	const tagMatch = metaTag.match(META_TAG_REGEX);
	const tagValid = Boolean(tagMatch?.groups?.code);
	const busy = isBusy(CHANNEL_ID) || submittingTag || finishing;

	// Re-entering step 2 after refreshing the page: if we already saved a
	// verification code, jump the user to sub-step 3 so they can finish.
	useEffect(() => {
		if (channel.record?.config?.domainVerificationCode) setDomainAdded(true);
	}, [channel.record?.config?.domainVerificationCode]);

	const saveTag = async () => {
		if (!tagValid || submittingTag) return;
		setSubmittingTag(true);
		setError(null);
		try {
			await update(CHANNEL_ID, { domainVerificationCode: tagMatch.groups.code });
			setVerificationSaved(true);
		} catch (err) {
			setError(err?.message || 'Could not save the verification code.');
		} finally {
			setSubmittingTag(false);
		}
	};

	const finishStep = async () => {
		setFinishing(true);
		setError(null);
		try {
			await enable(CHANNEL_ID);
			navigate(shopPath(`/channels/${CHANNEL_ID}/setup?step=create-shop`), { replace: true });
		} catch (err) {
			setError(err?.message || 'Could not enable the channel.');
			setFinishing(false);
		}
	};

	const subStep1Status = domainAdded ? 'completed' : 'in-progress';
	const subStep2Status = !domainAdded ? 'pending' : verificationSaved ? 'completed' : 'in-progress';
	const subStep3Status = !verificationSaved ? 'pending' : 'in-progress';

	return (
		<div className="space-y-6">
			<StepProgress index={2} />
			<StepHeader
				index={2}
				title="Verify your domain on Facebook"
				subtitle="Facebook needs to know your storefront belongs to you before it can show products from it. This happens once."
			/>

			<ol className="space-y-6 border-l border-border pl-6">
				<SubStepRow status={subStep1Status} title="Add your NearSt domain in Business Manager">
					<p>
						In Business Manager Settings, open{' '}
						<a
							href={`https://business.facebook.com/settings/owned-domains?business_id=${businessId}`}
							target="_blank"
							rel="noreferrer"
							className="text-action hover:underline"
						>
							Domains
							<ExternalLink className="ml-1 inline h-3 w-3" />
						</a>
						, then click <strong>Add</strong> → <strong>Create a new domain</strong> and enter{' '}
						<code className="rounded bg-muted px-1.5 py-0.5 text-xs font-semibold">{shopUrl}</code>.
					</p>
					<div className="flex flex-wrap items-center gap-3 pt-1">
						<Button onClick={() => setDomainAdded(true)} disabled={busy}>
							I've added it
						</Button>
					</div>
				</SubStepRow>

				<SubStepRow status={subStep2Status} title="Paste the verification meta tag back here">
					<p>
						Facebook shows a meta tag starting with{' '}
						<code className="rounded bg-muted px-1.5 py-0.5 text-xs font-semibold">
							&lt;meta name="facebook-domain-verification"
						</code>
						. Copy it from the <strong>Copy this meta tag</strong> section and paste below — we'll store it on your shop so
						Facebook can detect it.
					</p>
					<div className="space-y-2">
						<Input
							value={metaTag}
							onChange={(e) => setMetaTag(e.target.value)}
							placeholder='<meta name="facebook-domain-verification" content="…" />'
							className="font-mono text-xs"
						/>
						{!tagValid && metaTag.length > 0 ? (
							<p className="text-xs font-semibold text-destructive">
								That doesn't look like a valid meta tag — copy it exactly as Facebook shows it.
							</p>
						) : null}
					</div>
					<div className="flex flex-wrap items-center gap-3">
						<Button onClick={saveTag} disabled={!tagValid || submittingTag}>
							{submittingTag ? 'Saving…' : 'Save verification code'}
						</Button>
						<button
							type="button"
							onClick={() => setDomainAdded(false)}
							className="text-xs font-semibold text-muted-foreground hover:text-foreground"
						>
							Go back
						</button>
					</div>
				</SubStepRow>

				<SubStepRow status={subStep3Status} title='Click "Verify domain" on Facebook'>
					<p>
						On the same Domains page, click the <strong>Verify domain</strong> button at the bottom of the instructions.
						Facebook will scan your storefront for the meta tag and show a green <strong>Verified</strong> badge.
					</p>
					<div className="flex flex-wrap items-center gap-3">
						<Button onClick={finishStep} disabled={finishing}>
							{finishing ? 'Enabling…' : "I've verified — switch it on"}
							<ArrowRight className="ml-2 h-3.5 w-3.5" />
						</Button>
						<button
							type="button"
							onClick={() => setVerificationSaved(false)}
							className="text-xs font-semibold text-muted-foreground hover:text-foreground"
						>
							Go back
						</button>
					</div>
				</SubStepRow>
			</ol>

			{error ? <p className="text-sm font-semibold text-destructive">{error}</p> : null}
		</div>
	);
}

function StepThree({ channel }) {
	const { shopPath } = useShopPath();
	const businessId = channel.record?.config?.businessId;
	const catalogId = channel.record?.config?.catalogId;
	const onboardingUrl =
		businessId && catalogId
			? `https://business.facebook.com/commerce_manager/onboarding_landing/?business_id=${businessId}&catalogId=${catalogId}`
			: 'https://business.facebook.com/commerce_manager';

	return (
		<div className="space-y-6">
			<StepProgress index={3} />
			<StepHeader
				index={3}
				title="Create your shop on Meta Commerce Manager"
				subtitle="Final step: open Meta Commerce Manager and create a shop powered by the catalog we just connected. You only need to do this once per Facebook page or Instagram account."
			/>

			<p className="text-sm text-muted-foreground">
				Make sure you already have a Facebook Page or Instagram account linked to your Meta business — Commerce Manager will ask you
				to pick one.
			</p>

			<div className="flex flex-wrap items-center gap-3">
				<a
					href={onboardingUrl}
					target="_blank"
					rel="noreferrer"
					className="inline-flex items-center gap-2 rounded-full bg-action px-4 py-2.5 text-sm font-semibold text-action-foreground hover:opacity-90"
				>
					Open Commerce Manager <ExternalLink className="h-3.5 w-3.5" />
				</a>
				<Link
					to={shopPath(`/channels/${CHANNEL_ID}/manage`)}
					className="inline-flex items-center gap-2 rounded-full bg-foreground px-4 py-2.5 text-sm font-semibold text-background hover:opacity-90"
				>
					Done — go to Manage <ArrowRight className="h-3.5 w-3.5" />
				</Link>
			</div>
		</div>
	);
}
